import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  DialogActions,
  TablePagination,
  TextField,
} from "@mui/material";
import CustomTable from "../../components/CustomTable";
import CONSTANTS, {
  FORMS_ERROR_MSG,
  MODAL_ACTIONS,
  USER_GROUPS,
} from "../../constants/constants";
import { USER_GROUP, pushNotification } from "../../redux/actions";
import {
  DeleteRuntimeFormData,
  fetchRuntimeFormData,
  fetchRuntimeFormDetails,
  postRuntimeFormData,
} from "../../services/FormService";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FormPreview from "./FormPreview";
import Colors from "../../theme/colors.json";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const useStyles = makeStyles((theme) => ({
  contactRegisterContainer: {
    width: "100%",
    gap: 20,
    fontSize: theme.fontSizes.SMALL,
  },
  contactRegisterHeadingSection: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  HeadingSectionText: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: "15px",
    color: theme.palette.secondary.main,
  },
  contactRegisterSearchContainer: {
    display: "flex",
    flexDirection: "row",
    gap: 10,
  },
  contactRegisterTableSection: {
    marginTop: 20,
  },
  contactRegisterPaginationSection: {},
  tablePagination: {},
  deleteformContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 30,
  },
  deleteformBtnContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
  },
  deleteformContainerText: {
    fontSize: "22px",
    fontWeight: 700,
    lineHeight: "13px",
    color: theme.palette.primary.main,
  },
  dialogContentContainer: {
    display: "flex",
    justifyContent: "center",
  },
  actionBtnContainer: {
    justifyContent: "center !important",
  },
  previewTitle: {
    textAlign: "center",
    textTransform: "capitalize",
    fontWeight: 600,
    background: Colors.INSUR_BG,
    padding: "unset",
  },
  closeIcon: {
    position: "absolute",
    right: 8,
    // top: 8,
    color: theme.palette.grey[500],
  },
  searchField: {
    background: Colors.WHITE_BACKGROUND,
    paddingLeft: "0.5rem",
    borderRadius: "0.3rem",
  },
  commonBtnStyles: {
    background: Colors.WHITE_BACKGROUND,
    textTransform: "capitalize",
  },
  formDialog: {
    // zIndex: 9999,
  },
}));

function FormTable({ formId, columns }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));

  //pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalRecords, setTotalRecords] = React.useState(0);

  const [tableData, setTableData] = React.useState<any>([]);
  const [modalTitle, setModalTitle] = React.useState<any>("");
  const [selectedRow, setSelectedRow] = React.useState<any>({});
  const [searchTerm, setSearchTerm] = React.useState("");
  const [backupRowData, setBackupRowData] = React.useState<any>([]);

  const [formData, setFormData] = useState(null); //formData
  const [loading, setLoading] = useState(false); //loading stats
  const [isSubmit, setIsSubmit] = useState(false);
  const formRef = useRef<any>();

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setModalTitle("");
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = async (row) => {
    await setSelectedRow(row);
    await setModalTitle(MODAL_ACTIONS.EDIT);
    await handleClickOpen();
  };

  const handleDelete = (e) => {
    setModalTitle(MODAL_ACTIONS.DELETE);
    setSelectedRow(e);
    handleClickOpen();
  };

  const handleCreateNewClient = () => {
    setSelectedRow({});
    setModalTitle(MODAL_ACTIONS.CREATE);
    handleClickOpen();
  };

  //modal renderer
  const handleModalRender = (action) => {
    switch (action) {
      case MODAL_ACTIONS.CREATE:
      case MODAL_ACTIONS.EDIT:
        return (
          <FormPreview
            formData={formData}
            loading={loading}
            formRef={formRef}
            handleFormSubmit={handleFormSubmit}
            selectedRow={selectedRow}
          />
        );
      case MODAL_ACTIONS.DELETE:
        return <DeleteClient />;
      default:
        return null;
    }
  };

  //handle Submit trigger submit event of formio
  const handleSubmission = () => {
    if (formRef?.current) {
      formRef.current?.formio.submit();
    } else {
      pushNotification({
        isOpen: true,
        type: CONSTANTS.ERROR,
        message: FORMS_ERROR_MSG,
      });
      return;
    }
  };

  //save data to db
  const handleFormSubmit = async (submission) => {
    setIsSubmit(true);
    const payload = submission.data;
    if (selectedRow?.id) {
      const body = {
        formId: formId,
        id: selectedRow?.id,
        formData: payload,
      };
      const response: any = await postRuntimeFormData(body);
      if (response?.success) {
        setOpen(false);
        handleGetAllClients();
        dispatch(
          pushNotification({
            isOpen: true,
            type: CONSTANTS.SUCCESS,
            message: response?.message,
          })
        );
        setIsSubmit(false);
        handleClose();
      } else {
        setOpen(false);
        pushNotification({
          isOpen: true,
          type: CONSTANTS.ERROR,
          message: response?.message,
        });
        setIsSubmit(false);
        handleClose();
      }
    } else {
      const body = {
        formId: formId,
        formData: payload,
      };
      const response: any = await postRuntimeFormData(body);
      if (response?.success) {
        setOpen(false);
        handleGetAllClients();
        dispatch(
          pushNotification({
            isOpen: true,
            type: CONSTANTS.SUCCESS,
            message: response?.message,
          })
        );
        setIsSubmit(false);
      } else {
        setOpen(false);
        pushNotification({
          isOpen: true,
          type: CONSTANTS.ERROR,
          message: response?.message,
        });
        setIsSubmit(false);
      }
      handleClose();
    }
  };

  //fetch form
  const getFormData = async () => {
    setLoading(true);
    const response = await fetchRuntimeFormDetails(formId);
    if (response?.success) {
      setFormData(response?.data?.components);
      setLoading(false);
      dispatch(
        pushNotification({
          isOpen: true,
          message: response.message,
          type: CONSTANTS.SUCCESS,
        })
      );
    } else {
      dispatch(
        pushNotification({
          isOpen: true,
          message: response.message,
          type: CONSTANTS.ERROR,
        })
      );
      setLoading(false);
    }
  };

  //removing client/employee by row
  const DeleteClient = () => {
    const handleClientDeletion = async (selectedRow) => {
      const response = await DeleteRuntimeFormData(formId, selectedRow.id);
      if (response?.success) {
        handleClose();
        handleGetAllClients();
        dispatch(
          pushNotification({
            isOpen: true,
            message: response.message,
            type: CONSTANTS.SUCCESS,
          })
        );
      } else {
        handleClose();
        dispatch(
          pushNotification({
            isOpen: true,
            message: response.message,
            type: CONSTANTS.ERROR,
          })
        );
      }
    };

    return (
      <div className={classes.deleteformContainer}>
        <div>Are you sure, you want to delete this record ?</div>
        <div className={classes.deleteformBtnContainer}>
          <Button
            size="small"
            color="primary"
            variant="contained"
            onClick={() => handleClientDeletion(selectedRow)}
          >
            Delete
          </Button>
          <Button
            size="small"
            color="secondary"
            variant="contained"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  //table data Retriving
  const handleGetAllClients = async () => {
    const response = await fetchRuntimeFormData(formId, page, rowsPerPage);
    if (response?.success) {
      const filteredRows = response?.data?.content?.map((item) => {
        return {
          ...item?.formData,
          id: item?.id,
        };
      });

      setTableData(filteredRows);
      setBackupRowData(filteredRows);
      setTotalRecords(response?.data?.totalElements);
    } else {
      dispatch(
        pushNotification({
          isOpen: true,
          message: response.message,
          type: CONSTANTS.ERROR,
        })
      );
      setTotalRecords(0);
      setTableData([]);
      setBackupRowData([]);
    }
  };

  //search
  const handleWildcardSearch = (inputString, dataArray) => {
    const searchResults: any = [];

    // Loop through the data array
    for (const data of dataArray) {
      const escapedInput = inputString.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escape special regex characters
      // Convert the object values to a single string
      const dataString = Object.values(data).join(" ");

      // Perform wildcard search using regular expression
      const regex = new RegExp(escapedInput.replace(/\*/g, ".*"), "i");
      if (dataString.match(regex)) {
        searchResults.push(data);
      }
    }
    setTableData(searchResults);
  };

  useEffect(() => {
    if (searchTerm === "" || null || undefined) {
      setTableData(backupRowData);
    } else {
      handleWildcardSearch(searchTerm, backupRowData);
    }
  }, [searchTerm]);

  useEffect(() => {
    handleGetAllClients();
  }, [page, rowsPerPage]);

  useEffect(() => {
    getFormData();
  }, []);

  return (
    <div className={classes.contactRegisterContainer}>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth="xl"
        fullWidth
        className={classes.formDialog}
      >
        <DialogTitle
          className={classes.previewTitle}
          id="customized-dialog-title"
        >
          {modalTitle}
        </DialogTitle>

        <IconButton
          className={classes.closeIcon}
          aria-label="close"
          onClick={handleClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>{handleModalRender(modalTitle)}</DialogContent>
        <DialogActions className={classes.actionBtnContainer}>
          {modalTitle !== MODAL_ACTIONS.DELETE && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSubmission}
              disabled={isSubmit}
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </BootstrapDialog>

      <div className={classes.contactRegisterHeadingSection}>
        <div className={classes.contactRegisterSearchContainer}>
          <TextField
            variant="standard"
            placeholder="Search"
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              disableUnderline: true,
              classes: {
                input: classes.searchField,
              },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            color="primary"
            className={classes.commonBtnStyles}
            onClick={handleCreateNewClient}
          >
            Create New
          </Button>
        </div>
      </div>
      <div className={classes.contactRegisterTableSection}>
        <CustomTable
          columns={columns}
          data={tableData}
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          isEdit={true}
          isDelete={userGroup === USER_GROUPS.ADMIN ? true : false}
        />
      </div>
      <div className={classes.contactRegisterPaginationSection}>
        <TablePagination
          component="div"
          className={classes.tablePagination}
          count={totalRecords}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
        />
      </div>
    </div>
  );
}

export default FormTable;
